import React, { useContext, useCallback, useState, useRef } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { JSONSchema7 } from "json-schema";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";

import {
  useFormDataStore,
  useFormDataTemporal,
  useCanUndo,
  useCanRedo,
} from "../../store/formDataStore";
import { BodycamContext } from "../../context/BodycamContext";
import { useToast } from "../../shared/toast";
import { useInactivityTimer } from "../../shared/hooks/useInactivityTimer";
import { useLayout, usePageHeader } from "../../context/LayoutContext";

import { EditExtractedInformation } from "./EditExtractedInformation";
import { EditExtractedInformationSkeleton } from "../../components/skeletons/EditExtractedInformationSkeleton";
import { WarningDisplay } from "../../components/feedback/WarningDisplay";
import { InactivityModal } from "../../modals/InactivityModal";

import { useReportData } from "./hooks/useReportData";
import { useReportWebSocket } from "./hooks/useReportWebSocket";
import { useReportSaving } from "./hooks/useReportSaving";
import { EditSectionFooter } from "./components/EditSectionFooter";
import { ChatPanelContainer } from "../../components/chat/ChatPanelContainer";

import { eforceSchemaMapping } from "../../config/eforceSchemaMapping";
import { genericSchemaMapping } from "../../config/genericSchemaMapping";
import { AGENT_API_BASE_URL } from "../../shared/constants";
import { CoReviewBanner } from "../../components/coReview/CoReviewBanner";
import { ReviewHistoryDialog } from "../../components/coReview/ReviewHistoryDialog";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { useCoApprovalEnabled } from "../../hooks/useCoApprovalEnabled";
import { SectionCommentRail } from "../../components/coReview/SectionCommentRail";
import { NarrativeWithInlineComments } from "../../components/coReview/NarrativeWithInlineComments";
import {
  coApprove,
  coRetract,
  createCoComment,
  resolveCoComment as resolveCoCommentApi,
  unresolveCoComment as unresolveCoCommentApi,
} from "../../api/coReviewApi";
import { ApprovalSignatureDialog } from "../../components/coReview/ApprovalSignatureDialog";
import { RetractConfirmationDialog } from "../../components/coReview/RetractConfirmationDialog";
import Button from "@mui/material/Button";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import { useUserDirectory } from "../../api/userDirectory";
import { useAuth } from "@workos-inc/authkit-react";
import { useAuthContext } from "../../context/AuthContext";
import { Paper, Typography, Stack } from "@mui/material";

import { prettifySectionPath } from "../../utils/prettifySectionPath";
import {
  extractNarrativeTexts,
  splitCoComments,
} from "../../utils/coReviewHelpers";
import { getReportInformationProperties } from "./formUtils";

const EditSectionPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const { userInfo } = useContext(BodycamContext);
  const [userProfile] = userInfo;

  // Use global layout context for chat visibility
  const { setSidePanelOpen, setChatAvailable } = useLayout();

  // Ref to expose chat's sendMessage function to narrative widgets
  const chatSendMessageRef = useRef<
    ((message: string) => Promise<void>) | null
  >(null);

  // Ref to capture form data snapshot before agent changes (for change detection)
  const formDataSnapshotRef = useRef<EforceIncidentModel | null>(null);

  // Set page header in app bar
  usePageHeader({
    title: "Generated Report Editing",
    subtitle:
      "Use the Save button to save your changes. Sign off on every section to reach final review.",
    icon: <AutoAwesomeIcon />,
  });

  const toast = useToast();

  const formData = useFormDataStore((state) => state.formData);
  const setFormData = useFormDataStore((state) => state.setFormData);
  const setCurrentSection = useFormDataStore(
    (state) => state.setCurrentSection,
  );
  const { undo, redo } = useFormDataTemporal();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  // Custom data hooks
  const {
    reportID,
    videoData,
    setVideoData,
    videoLoadStatus,
    showRMSCommit,
    setShowRMSCommit,
    loading,
    jsonSchema,
    reportType,
    setReportType,
    lastModifiedTime,
    setLastModifiedTime,
    enableEditing,
    reFetchExtractedIncident,
    reportRms,
    coComments,
    setCoComments,
    coReturnedAt,
    reportStatus,
    reportOwnerUserId,
  } = useReportData();
  const coApprovalEnabled = useCoApprovalEnabled();

  const { user } = useAuth();
  const { organizationId } = useAuthContext();
  // Warm the user-name cache so comment author labels can resolve display names.
  useUserDirectory(organizationId ?? undefined);
  // Only the report's owning officer can finish review / confirm sections.
  // Other viewers (COs reviewing a submission) see no submit surface.
  // Fail closed: ownership is only granted on a positive match against a
  // known owner id. While the owner is still loading, isReportOwner is false
  // so owner-only controls stay hidden (brief flicker for the real owner,
  // but no leakage of owner UI to a CO viewer).
  const isOwnerKnown = reportOwnerUserId != null;
  const isReportOwner =
    !!user?.id && isOwnerKnown && reportOwnerUserId === user.id;
  // Officers reach EditSectionPage in CHANGES_REQUESTED to address CO feedback;
  // that's exactly when they need to mark comments resolved. The backend allows
  // PUT /comment/{id} in either IN_CO_REVIEW or CHANGES_REQUESTED. Gate on
  // isReportOwner so a CO viewer can't resolve the officer's comments — that's
  // an owner-only action; the CO's finalize path goes through canCoFinalize.
  const canResolveComments =
    reportStatus === ReportStatus.CHANGES_REQUESTED && isReportOwner;
  const officerUser = React.useMemo(
    () => ({
      userId: user?.id ?? "officer-demo-user",
      displayName:
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
        user?.email ||
        "Officer",
    }),
    [user],
  );

  const handleResolveComment = async (commentId: string) => {
    if (!reportID || !isReportOwner) return;
    try {
      await resolveCoCommentApi(reportID, commentId);
      setCoComments((prev) =>
        prev.map((c) =>
          c.commentId === commentId
            ? { ...c, resolvedAt: new Date().toISOString(), resolvedByUserId: officerUser.userId }
            : c,
        ),
      );
    } catch (err) {
      const e = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      toast.error(e?.response?.data?.error || e?.message || "Failed to resolve comment.");
    }
  };
  const handleUnresolveComment = async (commentId: string) => {
    if (!reportID || !isReportOwner) return;
    try {
      await unresolveCoCommentApi(reportID, commentId);
      setCoComments((prev) =>
        prev.map((c) =>
          c.commentId === commentId ? { ...c, resolvedAt: null, resolvedByUserId: null } : c,
        ),
      );
    } catch (err) {
      const e = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      toast.error(e?.response?.data?.error || e?.message || "Failed to unresolve comment.");
    }
  };

  const handleReplyComment = async (parentCommentId: string, text: string) => {
    if (!reportID) return;
    const parent = coComments.find((c) => c.commentId === parentCommentId);
    if (!parent) {
      toast.error("Cannot reply: parent comment is no longer visible.");
      return;
    }
    try {
      const created = await createCoComment({
        reportId: reportID,
        sectionPath: parent.sectionPath,
        fieldLabel: parent.fieldLabel,
        commentText: text,
        narrativeIndex: parent.narrativeIndex,
        charStart: parent.charStart,
        charEnd: parent.charEnd,
        quotedText: parent.quotedText,
        parentCommentId,
        authorName: officerUser.displayName,
      });
      setCoComments((prev) => [...prev, created]);
    } catch (err) {
      const e = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      toast.error(e?.response?.data?.error || e?.message || "Failed to post reply.");
    }
  };

  // TODO: re-enable "Fix with AI" once the real backend integration lands.
  // The handler below is a mock — `setTimeout(1200ms)` then auto-resolves the
  // comment. Commented out so the sparkle button is hidden in the v1 release.
  // Restore by uncommenting this block AND the three prop passes
  // (`onFixWithAi` / `onFixWithAiComment`) further down in this file.
  // const handleFixCommentWithAi = async (commentId: string) => {
  //   if (!reportID || !isReportOwner) return;
  //   await new Promise((r) => setTimeout(r, 1200));
  //   try {
  //     await resolveCoCommentApi(reportID, commentId);
  //     setCoComments((prev) =>
  //       prev.map((c) =>
  //         c.commentId === commentId
  //           ? {
  //               ...c,
  //               resolvedAt: new Date().toISOString(),
  //               resolvedByUserId: officerUser.userId,
  //             }
  //           : c,
  //       ),
  //     );
  //     toast.success("AI applied the suggestion. Comment marked resolved.");
  //   } catch (err) {
  //     const e = err as {
  //       response?: { data?: { error?: string } };
  //       message?: string;
  //     };
  //     toast.error(e?.response?.data?.error || e?.message || "Failed to apply AI fix.");
  //   }
  // };

  const {
    narrativeComments,
    generalComments,
    commentsBySection,
    narrativeCommentsByIndex,
  } = React.useMemo(() => splitCoComments(coComments), [coComments]);

  const narrativeTexts = React.useMemo(
    () => extractNarrativeTexts(formData as Record<string, unknown> | null),
    [formData],
  );

  // Officer-side prior-cycle history (spec §7) is shown on the CO side; the officer focuses
  // on the current cycle's comments while editing. Skip the history accordion here.

  // Wrapped fetch that captures snapshot before fetching (for agent change detection)
  const fetchReportDataWithSnapshot = useCallback(async () => {
    // Capture current form data BEFORE the fetch
    const currentFormData = useFormDataStore.getState().formData;
    formDataSnapshotRef.current = currentFormData
      ? JSON.parse(JSON.stringify(currentFormData))
      : null;

    console.log("[Agent Refresh] Captured snapshot before fetch:", {
      hasSnapshot: !!formDataSnapshotRef.current,
    });

    // Call the actual fetch
    await reFetchExtractedIncident();
  }, [reFetchExtractedIncident]);

  // Set chat availability on mount and control visibility based on editing
  // permission. The AI Assistant is also hidden when the report is in
  // CHANGES_REQUESTED so the v1 CO review release ships without AI behavior
  // on the rejected-report flow (see also the ChatPanelContainer gate below).
  const aiAssistantEnabled =
    enableEditing && reportStatus !== ReportStatus.CHANGES_REQUESTED;
  React.useEffect(() => {
    setChatAvailable(aiAssistantEnabled);
    setSidePanelOpen(aiAssistantEnabled);
    return () => {
      setChatAvailable(false);
      setSidePanelOpen(false);
    };
  }, [setChatAvailable, setSidePanelOpen, aiAssistantEnabled]);

  // Inactivity Timer State (managed here to coordinate with WS)
  const [inactivityExpiryTimestamp, setInactivityExpiryTimestamp] =
    useState<number>((Date.now() + 60000 * 5) / 1000);

  // Warnings State
  const [warnings, setWarnings] = useState<string[]>([]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [retractConfirmOpen, setRetractConfirmOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRetracting, setIsRetracting] = useState(false);

  // CO-only actions on a CHANGES_REQUESTED report: approve outright, or
  // retract the rejection so the report is back IN CO REVIEW. Officers
  // (report owners) never see these — they're editing to address feedback.
  // Require isOwnerKnown so the CO-finalize controls don't briefly appear to
  // the actual owner during the pending window where isReportOwner is still
  // false because reportOwnerUserId hasn't loaded yet.
  const canCoFinalize =
    coApprovalEnabled &&
    isOwnerKnown &&
    !isReportOwner &&
    reportStatus === ReportStatus.CHANGES_REQUESTED;

  const handleApprove = async () => {
    if (!reportID) return;
    setIsApproving(true);
    try {
      await coApprove({ reportId: reportID });
      toast.success("Report approved. Ready for RMS commit.");
      setSignatureDialogOpen(false);
      navigate(`/co-review/${reportID}`);
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || "Failed to approve report.");
    } finally {
      setIsApproving(false);
    }
  };

  const handleRetract = async () => {
    if (!reportID) return;
    setIsRetracting(true);
    try {
      await coRetract({ reportId: reportID });
      toast.success("Rejection retracted. Report is back in CO review.");
      setRetractConfirmOpen(false);
      navigate(`/co-review/${reportID}`);
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || "Failed to retract rejection.");
    } finally {
      setIsRetracting(false);
    }
  };

  // Inactivity Timer Hook
  const handleSessionTimeout = useCallback(() => {
    // Server drives timeout (close code 3008)
  }, []);

  const {
    showWarning: showTimeoutWarning,
    setWarningState,
    clearInactivityTimer,
  } = useInactivityTimer({
    countdownDuration: 1 * 60 * 1000,
    onTimeout: handleSessionTimeout,
  });

  // Saving Logic Hook
  const liveWebSocket = React.useRef<WebSocket | null>(null);

  const {
    isSaving,
    setIsSaving,
    saveDeferredRef,
    handleSaveAndExit,
    handleTimeoutSave,
    handleInstantSave,
    handleSaveCurrentChanges,
  } = useReportSaving({ liveWebSocket, showError: toast.error });

  // WebSocket Hook
  const { handleUserAction, continueSession } = useReportWebSocket({
    reportID,
    enableEditing,
    showError: toast.error,
    showSuccess: toast.success,
    showTimeoutWarning,
    setWarningState,
    setInactivityExpiryTimestamp,
    refetchIncident: reFetchExtractedIncident,
    setIsSaving,
    saveDeferredRef,
    setLastModifiedTime,
    setShowRMSCommit,
    setReportType,
    setVideoData,
    setWarnings,
    liveWebSocket,
  });

  const schemaMappingMap = {
    EF: eforceSchemaMapping,
    KAR: genericSchemaMapping,
  };

  const getSchemaObjectProperties = (schema: JSONSchema7): string[] => {
    if (!schema.properties) return [];
    const properties: string[] = [];
    for (const key in schema.properties) {
      const curProp = schema.properties[key] as JSONSchema7;
      if (curProp.type?.includes("array") || curProp.type?.includes("object")) {
        properties.push(key);
      }
    }
    return properties;
  };

  const handleUndo = () => {
    undo();
    handleUserAction("Undo change");

    if (!jsonSchema) return;

    const { currentSection } = useFormDataStore.getState();
    const sections = getSchemaObjectProperties(jsonSchema);
    const totalSections = 1 + sections.length;

    if (currentSection >= totalSections) {
      setCurrentSection(totalSections - 1);
    }
  };

  const handleRedo = () => {
    redo();
    handleUserAction("Redo change");

    if (!jsonSchema) return;

    const { currentSection } = useFormDataStore.getState();
    const sections = getSchemaObjectProperties(jsonSchema);
    const totalSections = 1 + sections.length;

    if (currentSection >= totalSections) {
      setCurrentSection(totalSections - 1);
    }
  };

  const handleUpdate = async (
    updatedModel: EforceIncidentModel,
    save: boolean,
  ) => {
    const newModel = JSON.parse(JSON.stringify(updatedModel));
    setFormData(newModel);
    if (save) {
      await handleSaveCurrentChanges();
    }
  };

  const handleSubmit = async (narrativeOnly: boolean) => {
    if (!formData) {
      toast.error("No report data loaded to submit.");
      return;
    }

    let reportData: Partial<EforceIncidentModel>;
    let baseSchema: JSONSchema7 | null | undefined;
    if (narrativeOnly && jsonSchema) {
      const { data: generalInfoData, schema: generalInfoSchema } =
        getReportInformationProperties(jsonSchema, formData);
      reportData = {
        ...generalInfoData,
        Narratives: formData["Narratives"],
      };
      baseSchema = {
        ...jsonSchema,
        properties: {
          ...generalInfoSchema.properties,
          Narratives: jsonSchema?.properties?.Narratives,
        },
      } as JSONSchema7;
    } else {
      reportData = formData;
      baseSchema = jsonSchema;
    }

    // save before navigating — in narrative-only mode only General Information
    // and Narratives are persisted; other sections are left untouched.
    await handleSaveCurrentChanges(narrativeOnly ? reportData : undefined);
    navigate(`/final-edits/${reportID}`, {
      state: {
        modelData: reportData,
        showRMSCommit: showRMSCommit,
        sparseInfo: {},
        jsonSchema: baseSchema,
        schemaMapping:
          schemaMappingMap[reportRms as keyof typeof schemaMappingMap],
      },
    });
  };

  const handleContinueSession = useCallback(() => {
    clearInactivityTimer();
    continueSession();
  }, [clearInactivityTimer, continueSession]);

  return (
    <>
      <WarningDisplay warnings={warnings} onDismiss={() => setWarnings([])} />
      <InactivityModal
        open={showTimeoutWarning}
        onContinue={handleContinueSession}
        onLogout={handleTimeoutSave}
        expiryTimestamp={inactivityExpiryTimestamp}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "calc(100vh - 96px)", // Full height minus app bar and padding
          overflow: "hidden", // Prevent outer scrolling
        }}
      >
        {/* Content Area + Sidebar Container */}
        <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
          {/* Main Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden", // Allow children to scroll
              p: 3, // Inner padding for content
            }}
          >
            {coApprovalEnabled && coComments.length > 0 && (
              <Box sx={{ mb: 2, flexShrink: 0 }}>
                <CoReviewBanner
                  comments={coComments}
                  returnedAt={coReturnedAt}
                  onViewHistory={() => setHistoryOpen(true)}
                />
              </Box>
            )}

            {canCoFinalize && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mb: 2, flexShrink: 0 }}
              >
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<VerifiedOutlinedIcon />}
                  onClick={() => setSignatureDialogOpen(true)}
                  loading={isApproving}
                >
                  Approve report
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<UndoOutlinedIcon />}
                  onClick={() => setRetractConfirmOpen(true)}
                  loading={isRetracting}
                >
                  Retract rejection
                </Button>
              </Stack>
            )}

            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: "auto",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {coComments.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? "rgba(209, 154, 102, 0.04)"
                        : "rgba(255, 167, 38, 0.04)",
                  }}
                >
                  {generalComments.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb:
                          narrativeComments.length > 0 ||
                          commentsBySection.length > 0
                            ? 1.5
                            : 0,
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          color: "warning.dark",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        General comments
                      </Typography>
                      <SectionCommentRail
                        sectionPath="$"
                        comments={generalComments}
                        mode={canResolveComments ? "officer" : "officer-readonly"}
                        reportOwnerUserId={reportOwnerUserId ?? undefined}
                        onResolve={handleResolveComment}
                        onUnresolve={handleUnresolveComment}
                        // TODO: re-enable when Fix with AI is implemented.
                        // onFixWithAi={handleFixCommentWithAi}
                        onReply={handleReplyComment}
                      />
                    </Paper>
                  )}

                  {narrativeTexts.length > 0 && narrativeComments.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: commentsBySection.length > 0 ? 1.5 : 0,
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          color: "warning.dark",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Narrative inline comments
                      </Typography>
                      <Stack spacing={2}>
                        {narrativeTexts.map((n) => {
                          const items = narrativeCommentsByIndex.get(n.index);
                          if (!items || items.length === 0) return null;
                          return (
                            <Box key={n.index} data-co-narrative-index={n.index}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Narrative {n.index + 1}
                              </Typography>
                              <NarrativeWithInlineComments
                                narrativeIndex={n.index}
                                text={n.text}
                                comments={items}
                                mode={canResolveComments ? "officer" : "co-readonly"}
                                reportOwnerUserId={reportOwnerUserId ?? undefined}
                                onResolveComment={handleResolveComment}
                                onUnresolveComment={handleUnresolveComment}
                                // TODO: re-enable when Fix with AI is implemented.
                                // onFixWithAiComment={handleFixCommentWithAi}
                                onReplyComment={handleReplyComment}
                              />
                            </Box>
                          );
                        })}
                      </Stack>
                    </Paper>
                  )}

                  {commentsBySection.length > 0 && (
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 1,
                        p: 1.5,
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          color: "warning.dark",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Section comments
                      </Typography>
                      {commentsBySection.map(([section, items]) => (
                        <SectionCommentRail
                          key={section}
                          sectionPath={section}
                          fieldLabel={prettifySectionPath(section)}
                          comments={items}
                          mode={canResolveComments ? "officer" : "officer-readonly"}
                          reportOwnerUserId={reportOwnerUserId ?? undefined}
                          onResolve={handleResolveComment}
                          onUnresolve={handleUnresolveComment}
                          // TODO: re-enable when Fix with AI is implemented.
                          // onFixWithAi={handleFixCommentWithAi}
                          onReply={handleReplyComment}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              {loading || !formData ? (
                <EditExtractedInformationSkeleton />
              ) : (
                <EditExtractedInformation
                  formData={formData}
                  jsonSchema={jsonSchema as JSONSchema7}
                  schemaMapping={
                    schemaMappingMap[reportRms as keyof typeof schemaMappingMap]
                  }
                  onUpdate={handleUpdate}
                  onSubmit={handleSubmit}
                  videoData={videoData}
                  videoLoadStatus={videoLoadStatus}
                  handleUserActivity={handleUserAction}
                  liveWebSocket={liveWebSocket}
                  allowEditing={enableEditing}
                  isReportOwner={isReportOwner}
                  isSaving={isSaving}
                  handleSaveChanges={handleSaveCurrentChanges}
                  reportId={reportID}
                  reportType={reportType}
                  agentApiEndpoint={AGENT_API_BASE_URL}
                  fetchReportData={fetchReportDataWithSnapshot}
                  formDataSnapshotRef={formDataSnapshotRef}
                  userProfile={userProfile}
                  chatSendMessageRef={chatSendMessageRef}
                  setShowChat={setSidePanelOpen}
                  showRMSCommit={showRMSCommit}
                />
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <EditSectionFooter
                enableEditing={enableEditing}
                lastModifiedTime={lastModifiedTime}
                isSaving={isSaving}
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={handleUndo}
                onRedo={handleRedo}
                onInstantSave={handleInstantSave}
                onSaveAndExit={handleSaveAndExit}
              />
            </Box>
          </Box>

          {/* Right Chat Sidebar - only show when user can edit (not read-only mode).
              TODO: re-enable AI Assistant for rejected reports once the AI flow
              handles CO feedback context. Hidden for now in CHANGES_REQUESTED so
              the v1 CO review release ships without AI behavior on that path. */}
          {isLgUp &&
            reportID &&
            enableEditing &&
            reportStatus !== ReportStatus.CHANGES_REQUESTED && (
              <ChatPanelContainer
                mode="chat"
                chatProps={{
                  liveWebSocket,
                  agentApiEndpoint: AGENT_API_BASE_URL,
                  reportId: reportID,
                  reportType,
                  handleSaveChanges: handleSaveCurrentChanges,
                  fetchReportData: fetchReportDataWithSnapshot,
                  showError: toast.error,
                  onSendMessageRefReady: (fn) => {
                    chatSendMessageRef.current = fn;
                  },
                }}
              />
            )}
        </Box>
      </Box>
      {reportID && coApprovalEnabled && (
        <ReviewHistoryDialog
          open={historyOpen}
          reportId={reportID}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      <ApprovalSignatureDialog
        open={signatureDialogOpen}
        coName={officerUser.displayName}
        onClose={() => setSignatureDialogOpen(false)}
        onConfirm={handleApprove}
        loading={isApproving}
      />

      <RetractConfirmationDialog
        open={retractConfirmOpen}
        onClose={() => setRetractConfirmOpen(false)}
        onConfirm={handleRetract}
        loading={isRetracting}
      />
    </>
  );
};

export default EditSectionPage;
